import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Smartphone, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

export function InstallPWA() {
    const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Only show install prompt to authenticated users
    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setIsAuthenticated(!!session);
        });
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setIsAuthenticated(!!session);
            if (!session) setIsVisible(false); // hide on logout
        });
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        // Never show if already running as an installed PWA
        if (window.matchMedia('(display-mode: standalone)').matches) return;

        const handler = (e: Event) => {
            // Only intercept native banner for signed-in users where we show custom CTA.
            if (!isAuthenticated) return;
            e.preventDefault();
            setInstallPrompt(e as BeforeInstallPromptEvent);
            setIsVisible(true);
        };

        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [isAuthenticated]);

    const handleInstall = React.useCallback(async () => {
        if (!installPrompt) {
            toast.info("Manual Installation", {
                description: "To install: Click your browser's menu (three dots) and select 'Install App' or 'Add to Home Screen'."
            });
            return;
        }

        // Show the prompt
        installPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await installPrompt.userChoice;
        console.log(`[PWA] User response to install prompt: ${outcome}`);

        // We've used the prompt, and can't use it again, throw it away
        setInstallPrompt(null);
        setIsVisible(false);

        if (outcome === 'accepted') {
            toast.success('DCODE App installing...', {
                description: 'You can now access the app from your home screen.'
            });
        }
    }, [installPrompt]);

    useEffect(() => {
        if (!handleInstall) return;
        const triggerHandler = () => {
            handleInstall();
        };
        window.addEventListener('trigger-pwa-install', triggerHandler);
        return () => window.removeEventListener('trigger-pwa-install', triggerHandler);
    }, [handleInstall]);

    if (!isVisible || !isAuthenticated) return null;

    return (
        <div className="fixed bottom-0 right-0 z-40 md:bottom-6 md:right-6 md:z-50 animate-in fade-in slide-in-from-bottom-10 duration-500 m-3 md:m-0 w-auto md:w-auto safe-area-inset-bottom">
            <div className="relative group">
                {/* Decorative Glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-blue-600 rounded-lg md:rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000" />

                <div className="relative flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-slate-900 border border-border rounded-lg md:rounded-2xl p-2 sm:p-3 md:p-2 md:pr-4 shadow-2xl backdrop-blur-xl max-w-sm md:max-w-none">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsVisible(false)}
                        className="h-5 w-5 sm:h-6 sm:w-6 rounded-full hover:bg-muted/50 text-muted-foreground absolute -top-1.5 -right-1.5 md:-top-2 md:-right-2 bg-slate-900 border border-border touch-target"
                    >
                        <X className="h-3 w-3" />
                    </Button>

                    <div className="flex items-center gap-2 sm:gap-4">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg md:rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                            <div className="relative">
                                <Smartphone className="h-6 w-6 text-emerald-400" />
                                <div className="absolute -top-1 -right-1 h-3 w-3 bg-emerald-500 rounded-full border-2 border-slate-900 animate-pulse" />
                            </div>
                        </div>

                        <div className="flex flex-col">
                            <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-widest leading-none mb-1">DCODE Desktop/Mobile</span>
                            <h4 className="text-sm font-semibold text-foreground uppercase tracking-tight">System Installation</h4>
                        </div>

                        <Button
                            onClick={handleInstall}
                            className="ml-2 h-10 px-6 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold uppercase text-[10px] tracking-widest rounded-xl transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
                        >
                            <Download className="h-3.5 w-3.5 mr-2" />
                            Install App
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
